import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { API_BASE } from "@/lib/api";

function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${u}`;
  return u;
}

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    for (const [k, v] of Object.entries(attrs.match || {})) el.setAttribute(k, v);
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs.set || {})) el.setAttribute(k, v);
}

function upsertLink(rel, href, type) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  if (type) el.setAttribute("type", type);
}

export default function SiteHead() {
  const { siteSettings } = useAuth();

  useEffect(() => {
    if (!siteSettings) return;

    const title = siteSettings.meta_title || siteSettings.site_name || "Start-A-TE";
    const description = siteSettings.meta_description || "";
    const keywords = siteSettings.meta_keywords || "";
    const favicon = resolveUrl(siteSettings.favicon_url);
    const ogImage = resolveUrl(siteSettings.og_image_url) || resolveUrl(siteSettings.logo_url);

    document.title = title;

    upsertMeta('meta[name="description"]', {
      match: { name: "description" },
      set: { content: description },
    });
    upsertMeta('meta[name="keywords"]', {
      match: { name: "keywords" },
      set: { content: keywords },
    });

    // Open Graph
    upsertMeta('meta[property="og:title"]', {
      match: { property: "og:title" },
      set: { property: "og:title", content: title },
    });
    upsertMeta('meta[property="og:description"]', {
      match: { property: "og:description" },
      set: { property: "og:description", content: description },
    });
    upsertMeta('meta[property="og:type"]', {
      match: { property: "og:type" },
      set: { property: "og:type", content: "website" },
    });
    if (ogImage) {
      upsertMeta('meta[property="og:image"]', {
        match: { property: "og:image" },
        set: { property: "og:image", content: ogImage },
      });
    }
    // Twitter card
    upsertMeta('meta[name="twitter:card"]', {
      match: { name: "twitter:card" },
      set: { name: "twitter:card", content: ogImage ? "summary_large_image" : "summary" },
    });
    upsertMeta('meta[name="twitter:title"]', {
      match: { name: "twitter:title" },
      set: { name: "twitter:title", content: title },
    });
    upsertMeta('meta[name="twitter:description"]', {
      match: { name: "twitter:description" },
      set: { name: "twitter:description", content: description },
    });
    if (ogImage) {
      upsertMeta('meta[name="twitter:image"]', {
        match: { name: "twitter:image" },
        set: { name: "twitter:image", content: ogImage },
      });
    }

    // Favicon: allow ico/png/svg
    if (favicon) {
      const ext = favicon.split(".").pop().toLowerCase();
      const type = ext === "svg" ? "image/svg+xml" : ext === "ico" ? "image/x-icon" : `image/${ext === "jpg" ? "jpeg" : ext}`;
      upsertLink("icon", favicon, type);
      upsertLink("shortcut icon", favicon, type);
      upsertLink("apple-touch-icon", favicon);
    }
  }, [siteSettings]);

  return null;
}
